// The string drawer (spec/dashboard.md section 9.2), `?string=<id>` on any localization tab: Key, Group, Description,
// Export, one textarea per enabled language, and Where used (the documents' fields and the products that reference it).
// Edits stage into the same draft as the grid; Cancel reverts the drawer's edits (section 4.4); Done keeps them.
import { useEffect, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import { useAuth } from '../../auth/AuthContext';
import { useContent } from '../../content/ContentContext';
import { humanizeKey, languageHeader } from '../../content/localization/languages';
import { editString, fullKeyOf, removeString, valuesOf, type StringRow } from '../../content/localization/strings';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { EmptyState } from '../../ui/Banner';
import { Button } from '../../ui/Button';
import { Badge, draftTone, draftWord } from '../../ui/Chip';
import { Field } from '../../ui/Field';
import { AlertCircle, AlertTriangle, ExternalLink, FileText, Languages, ShoppingBag } from '../../ui/icons';
import { CommitTextInput, Switch } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { useQuery } from '../../workspace/cache';
import { useWorkspace, useWorkspaceVersion } from '../../workspace/react';
import { entityKey, isTempId } from '../../workspace/Workspace';
import { GroupPicker } from './GroupPicker';

export function StringDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { org, game, can } = useGame();
  const { branch = '' } = useParams();
  const { client } = useAuth();
  const catalog = useContent();
  const workspace = useWorkspace();
  useWorkspaceVersion(workspace);
  const canWrite = can('content_write') && !workspace.isProtected;
  const [params] = useSearchParams();
  const draft = workspace.getDraft(entityKey('string', id));
  const server = catalog.strings.get(id);
  const row = (draft?.current as unknown as StringRow | undefined) ?? server;
  const mark = useRef<number | null>(null);
  useEffect(() => {
    mark.current = workspace.mark();
  }, [id, workspace]);
  const whereRef = useRef<HTMLDivElement>(null);
  const focusWhere = params.get('where') === '1';
  useEffect(() => {
    if (focusWhere) whereRef.current?.scrollIntoView?.({ block: 'start' });
  }, [focusWhere, row?.id]);

  const refsQ = useQuery(isTempId(id) || !server ? null : `branch:${game.id}:${branch}:string-references:${id}`, () => client.data(ops.listLocalizationStringReferences, { path: { org, game: game.slug, branch, string: id } }), { head: workspace.head });

  const cancel = () => {
    if (mark.current !== null) workspace.revertTo(mark.current);
    onClose();
  };

  if (!row) {
    return (
      <Modal title="Localization string" open drawer onClose={onClose}>
        <div data-testid="string-drawer">
          {catalog.loading ? (
            <div className="skeleton-rows" role="status" aria-label="Loading">
              {[0, 1, 2].map((i) => (
                <div key={i} className="skeleton-row">
                  <span className="skeleton" />
                  <span className="skeleton" />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={<Languages />} title={isTempId(id) ? 'This draft no longer exists' : `No string ${id} on this branch`} body="It may have been deleted or discarded." />
          )}
        </div>
      </Modal>
    );
  }
  const deleted = draft?.status === 'deleted';
  const readOnly = !canWrite || deleted;
  const edit = (path: string, value: unknown) => editString(workspace, catalog, row, path, value);
  const values = valuesOf(row);
  const refs = refsQ.data;

  return (
    <Modal
      title={
        <span className="row">
          <span>{row.key ? fullKeyOf(row) : `${row.group}/(new string)`}</span>
          {draft && <Badge tone={draftTone[draft.status]}>{draftWord[draft.status]}</Badge>}
        </span>
      }
      open
      drawer
      onClose={onClose}
      footer={
        <>
          {!readOnly && (
            <Button
              variant="danger"
              onClick={() => {
                removeString(workspace, catalog, row);
                onClose();
              }}
            >
              Delete
            </Button>
          )}
          <span className="spacer" />
          <Button onClick={cancel}>Cancel</Button>
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </>
      }
    >
      <div className="loc-drawer" data-testid="string-drawer">
        {draft?.error && (
          <div className="loc-alert" role="alert">
            <AlertCircle className="loc-alert-icon" aria-hidden="true" />
            <div>{draft.error.message}</div>
          </div>
        )}
        {deleted && (
          <div className="loc-note warning">
            <AlertTriangle className="loc-alert-icon" aria-hidden="true" />
            <div>The deletion is staged; Save All commits it.</div>
          </div>
        )}
        <section className="loc-drawer-section">
          <div className="section-head">
            <h3>String</h3>
          </div>
          <div className="form-grid">
            <Field label="Key" required>
              <CommitTextInput className="mono" value={row.key} onCommit={(v) => v.trim() && edit('key', v.trim())} disabled={readOnly} aria-label="Key" placeholder="KEY_NAME" />
            </Field>
            <Field label="Group">
              <GroupPicker value={row.group} groups={catalog.groups} onCommit={(g) => edit('group', g)} disabled={readOnly} />
            </Field>
            <Field className="span-2" label="Description">
              <CommitTextInput value={row.description ?? ''} onCommit={(v) => edit('description', v === '' ? null : v)} disabled={readOnly} aria-label="Description" />
            </Field>
            <div className="field span-2">
              <Switch label="Export" checked={row.export !== false} disabled={readOnly} onChange={(v) => edit('export', v)} />
            </div>
          </div>
        </section>
        <section className="loc-drawer-section">
          <div className="section-head">
            <h3>Texts</h3>
            <span className="chip">{catalog.languages.length}</span>
          </div>
          <div className="form-grid loc-drawer-values">
            {catalog.languages.map((l) => {
              const header = `${languageHeader(l.code, l.name)}${l.code === catalog.defaultLanguage ? ' *' : ''}`;
              return (
                <Field key={l.code} label={header} required={l.code === catalog.defaultLanguage}>
                  <CommitTextInput multiline value={values[l.code] ?? ''} onCommit={(v) => edit(`values.${l.code}`, v)} disabled={readOnly} aria-label={header} placeholder={l.code === catalog.defaultLanguage ? humanizeKey(row.key) : 'missing'} />
                </Field>
              );
            })}
          </div>
        </section>
        <section ref={whereRef} className="loc-drawer-section loc-where">
          <div className="section-head">
            <h3 className="section-title">Where used</h3>
            {refs && <span className="chip">{refs.documents.length + refs.products.length}</span>}
          </div>
          {isTempId(id) || !server ? (
            <p className="muted small">A new string is referenced by nothing until it is saved.</p>
          ) : refsQ.loading && !refs ? (
            <div className="skeleton-rows" role="status" aria-label="Loading">
              {[0, 1].map((i) => (
                <div key={i} className="skeleton-row">
                  <span className="skeleton" />
                  <span className="skeleton" />
                </div>
              ))}
            </div>
          ) : refsQ.error ? (
            <p className="error small">{refsQ.error instanceof ApiError ? refsQ.error.message : String(refsQ.error)}</p>
          ) : refs && refs.documents.length === 0 && refs.products.length === 0 ? (
            <p className="muted small">No document or product references this string.</p>
          ) : (
            refs && (
              <ul className="loc-where-list list-pane">
                {refs.documents.map((d) => {
                  const doc = catalog.resolve(d.documentId);
                  const label = doc ? catalog.label(doc) : d.documentId.slice(0, 8);
                  return (
                    <li key={`d:${d.documentId}:${d.path}`} className="list-row static">
                      <FileText className="icon muted" aria-hidden="true" />
                      <span className="text">
                        <span className="title">
                          <Link to={`${routes.documents(org, game.slug, branch, d.template)}?doc=${encodeURIComponent(d.documentId)}`} aria-label={`Open ${label}`}>
                            {label} <ExternalLink className="icon-s" aria-hidden="true" />
                          </Link>
                        </span>
                        <span className="sub">
                          {d.template.split('.').pop()} · <code>{d.path}</code>
                        </span>
                      </span>
                    </li>
                  );
                })}
                {refs.products.map((p) => {
                  const product = catalog.products.get(p.productId);
                  const label = product ? product.name : p.productId.slice(0, 8);
                  return (
                    <li key={`p:${p.productId}:${p.field}`} className="list-row static">
                      <ShoppingBag className="icon muted" aria-hidden="true" />
                      <span className="text">
                        <span className="title">
                          <Link to={routes.branch(org, game.slug, branch, 'products')} aria-label={`Open product ${label}`}>
                            {label} <ExternalLink className="icon-s" aria-hidden="true" />
                          </Link>
                        </span>
                        <span className="sub">
                          product · <code>{p.field}</code>
                        </span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )
          )}
        </section>
      </div>
    </Modal>
  );
}
